export interface DriverLicenseResult {
  licenseNumber?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dateOfBirth?: string;
  issueDate?: string;
  expirationDate?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  sex?: string;
  height?: string;
  issuingCountry?: string;
  aamvaVersion?: string;
  unknownFields?: Record<string, string>;
}

const knownFieldCodes = new Set([
  "DAQ",
  "DCS",
  "DAC",
  "DAD",
  "DBB",
  "DBA",
  "DBD",
  "DAG",
  "DAI",
  "DAJ",
  "DAK",
  "DBC",
  "DAU"
]);

const fieldCodePattern = /^[A-Z0-9]{3}/;

export function parseAamvaPayload(payload: string): DriverLicenseResult | null {
  const text = normalizePayload(payload);
  if (!looksLikeAamva(text)) return null;

  const fields = extractFields(text);
  if (!hasDriverLicenseSignal(fields)) return null;

  const result: DriverLicenseResult = {
    licenseNumber: cleanValue(fields.DAQ),
    firstName: cleanValue(fields.DAC),
    middleName: cleanValue(fields.DAD),
    lastName: cleanValue(fields.DCS),
    dateOfBirth: normalizeAamvaDate(fields.DBB),
    issueDate: normalizeAamvaDate(fields.DBD),
    expirationDate: normalizeAamvaDate(fields.DBA),
    sex: normalizeSex(fields.DBC),
    height: cleanValue(fields.DAU),
    ...parseHeader(text)
  };

  const address = {
    street: cleanValue(fields.DAG),
    city: cleanValue(fields.DAI),
    state: cleanValue(fields.DAJ),
    postalCode: normalizePostalCode(fields.DAK)
  };
  if (Object.values(address).some(Boolean)) result.address = address;

  const unknownFields = Object.fromEntries(
    Object.entries(fields).filter(([code, value]) => !knownFieldCodes.has(code) && Boolean(cleanValue(value)))
  );
  if (Object.keys(unknownFields).length > 0) result.unknownFields = unknownFields;

  return hasDisplayableFields(result) ? result : null;
}

export function formatDriverLicenseForCopy(result: DriverLicenseResult): string {
  const name = [result.firstName, result.middleName, result.lastName].filter(Boolean).join(" ");
  const lines = [
    name,
    result.licenseNumber ? `DL: ${result.licenseNumber}` : "",
    result.dateOfBirth ? `DOB: ${formatDisplayDate(result.dateOfBirth)}` : "",
    result.expirationDate ? `Expires: ${formatDisplayDate(result.expirationDate)}` : "",
    result.address?.street ?? "",
    [result.address?.city, [result.address?.state, result.address?.postalCode].filter(Boolean).join(" ")].filter(Boolean).join(", ")
  ];
  return lines.filter(Boolean).join("\n");
}

export function formatDisplayDate(value?: string): string {
  if (!value) return "";
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return value;
  return `${match[2]}/${match[3]}/${match[1]}`;
}

function normalizePayload(payload: string): string {
  return payload.replace(/\r\n?/g, "\n").replace(/\u001e/g, "\n").trim();
}

function looksLikeAamva(text: string): boolean {
  return /^@/.test(text) || /\bANSI\s*\d{6}/.test(text) || /\bDAQ[^\n]+/.test(text);
}

function extractFields(text: string): Record<string, string> {
  const fields: Record<string, string> = {};
  for (const rawLine of text.split("\n")) {
    const line = rawLine.trim();
    if (line.length < 4) continue;
    if (line === "@" || line.startsWith("ANSI")) {
      const embedded = findEmbeddedKnownField(line);
      if (!embedded) continue;
      fields[embedded.code] = embedded.value;
      continue;
    }
    const code = line.slice(0, 3);
    if (!fieldCodePattern.test(code) || (code[0] !== "D" && code[0] !== "Z")) continue;
    const value = line.slice(3).trim();
    if (value) fields[code] = value;
  }
  return fields;
}

function findEmbeddedKnownField(line: string): { code: string; value: string } | null {
  for (const code of knownFieldCodes) {
    const index = line.indexOf(code);
    if (index >= 0) return { code, value: line.slice(index + 3).trim() };
  }
  return null;
}

function hasDriverLicenseSignal(fields: Record<string, string>): boolean {
  const identityFields = ["DAQ", "DCS", "DAC", "DBB"].filter((code) => cleanValue(fields[code])).length;
  return identityFields >= 2 && Boolean(cleanValue(fields.DAQ) || (cleanValue(fields.DCS) && cleanValue(fields.DAC)));
}

function hasDisplayableFields(result: DriverLicenseResult): boolean {
  return Boolean(result.licenseNumber || result.firstName || result.lastName || result.dateOfBirth || result.address);
}

function parseHeader(text: string): Pick<DriverLicenseResult, "issuingCountry" | "aamvaVersion"> {
  const header = text.match(/\bANSI\s*(\d{6})(\d{2})?/);
  if (!header) return {};
  return {
    issuingCountry: "USA",
    aamvaVersion: header[2]
  };
}

function cleanValue(value?: string): string | undefined {
  const cleaned = value?.trim();
  return cleaned || undefined;
}

function normalizeAamvaDate(value?: string): string | undefined {
  const cleaned = cleanValue(value);
  if (!cleaned) return undefined;
  const compact = cleaned.replace(/\D/g, "");
  if (!/^\d{8}$/.test(compact)) return cleaned;
  const month = compact.slice(0, 2);
  const day = compact.slice(2, 4);
  const year = compact.slice(4, 8);
  return `${year}-${month}-${day}`;
}

function normalizePostalCode(value?: string): string | undefined {
  const cleaned = cleanValue(value);
  if (!cleaned) return undefined;
  const compact = cleaned.replace(/\s+/g, "");
  if (/^\d{9}$/.test(compact)) return `${compact.slice(0, 5)}-${compact.slice(5)}`;
  return compact;
}

function normalizeSex(value?: string): string | undefined {
  const cleaned = cleanValue(value);
  if (cleaned === "1") return "M";
  if (cleaned === "2") return "F";
  if (cleaned === "9") return "X";
  return cleaned;
}
